import _ from "lodash";
import PropTypes from "prop-types";

export const focusable =
'a:not([disabled]), button:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';

/**
 * @deprecated The use of e.keyCode with this HTML element keys constant is now deprecated. 
 * Please transition to using the "KEYBOARD_KEYS" constant which utilizes e.key.
 * 
 * Example:
 * 
 * Deprecated usage: e.keyCode === keys.enter;
 * 
 * Recommended usage: e.key === KEYBOARD_KEYS.ENTER;
 */
export const keys = {
    tab: 9,
    enter: 13,
    escape: 27,
    space: 32,
    left: 37,
    up: 38,
    right: 39,
    down: 40
};

/**
 * Constant object mapping the names of keyboard keys to their corresponding `e.key` values.
 * This constant should be used in keyboard event handlers to check which key was pressed.
 * 
 * Example usage:
 * 
 * if (e.key === KEYBOARD_KEYS.ENTER) {
 *   // The Enter key was pressed
 * }
 * 
 * Note: The SPACE_BAR and SPACE_BAR_ALTERNATIVE properties both represent the Space key,
 * as the `e.key` value for the Space key can be either " " or "Space" depending on the browser.
 * "Meta" (also known as the "Windows" key on Windows or "Command" key on Mac).
 */
export const KEYBOARD_KEYS = {
    // Modifier keys
    SHIFT: 'Shift',
    CONTROL: 'Control',
    ALT: 'Alt',
    META: 'Meta',
    CAPSLOCK: 'CapsLock',

    // Navigation keys
    ARROW_UP: 'ArrowUp',
    ARROW_DOWN: 'ArrowDown',
    ARROW_LEFT: 'ArrowLeft',
    ARROW_RIGHT: 'ArrowRight',
    HOME: 'Home',
    END: 'End',
    PAGE_UP: 'PageUp',
    PAGE_DOWN: 'PageDown',

    // Editing keys
    BACKSPACE: 'Backspace',
    DELETE: 'Delete',
    INSERT: 'Insert',
    TAB: 'Tab',
    ENTER: 'Enter',

    // Other keys
    ESCAPE: 'Escape',
    SPACE_BAR: ' ',
    SPACE_BAR_ALTERNATIVE: 'Space',
};

export const saveToDisk = ({contents, fileName}) => {
    if (window.navigator.msSaveOrOpenBlob) { // EDGE workaround
        const blob = new Blob([decodeURIComponent(encodeURI(contents))], { type: 'text/csv;charset=utf-8;' });
        navigator.msSaveBlob(blob, fileName);
    } else {
        const element = document.createElement('a');
        element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(contents)}`);
        element.setAttribute('download', fileName);
        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }
}

/**
 Extracts error from given error object
 Handles:
    - errors from axios (have e.response.data)
        => error can the the response payload, message property of response payload, or nested payload.error.message
    - error which is Error/object that has message property
    - error which is only a string
 * @returns {*|string}
 */
export const extractErrorMessage = (e, errMsgKey) => {
    const data = e?.response?.data;
    if (_.isString(data)) {
        return data;
    } else if (_.isObject(data)) {
        if (data.message) {
            return data.message
        } else if (_.isString(data?.error)) {
            return data.error;
        } else if (data && _.isString(data[errMsgKey])) {
            return data[errMsgKey];
        } else {
            return data?.error?.message || "Unknown";
        }

    } else if (_.isString(e)) {
        return e;
    } else {
        return e.message;
    }
};

export const readFileFromEvent = (e) => {
    return new Promise((resolve) => {
        const file = e.target.files[0];
        if (file) {
            const fileReader = new FileReader();
            fileReader.readAsText(file);
            fileReader.onloadend = () => {
                resolve({
                    fileName: file.name,
                    contents: fileReader.result,
                    fileSize: file.size
                })
            };
        }
    });
};

export const matchPropType = (propType) => {
    let isRequired = false;

    const type = _.findKey(PropTypes, (val) => {
        if(propType === val.isRequired) {
            isRequired = true;
            return true;
        } else {
            return propType === val
        }
    }) || "uknown";

    return {
        type,
        isRequired
    }
};
